import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import axios from 'axios';

import BudgetsByTagTable from './BudgetsByTagTable';

import Alert from 'CommonComponents/Alert';
import Button from 'CommonComponents/Button';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';

import DownloadIcon from 'Assets/icons/download.svg';

function Header({ config, title }) {
    return (
        <div className="tw-flex tw-flex-col tw-gap-y-2">
            <nav aria-label={$t('Breadcrumb')} className="tw-flex tw-items-center tw-gap-1 tw-text-sm tw-text-charcoal-75">
                <a href={config.reportCentreUrl} className="tw-text-blue hover:tw-underline">
                    {$t('Report Centre')}
                </a>
                <span aria-hidden="true">&rsaquo;</span>
                <a href={config.reconfigureUrl} className="tw-text-blue hover:tw-underline">
                    {$t('Budgets by Tag')}
                </a>
                <span aria-hidden="true">&rsaquo;</span>
                <span>{$t('On-screen')}</span>
            </nav>
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                <h1 className="tw-m-0 tw-text-2.5xl tw-font-semibold tw-text-charcoal">{title}</h1>
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                    <Button as="a" href={config.exportUrl} variant="outline-neutral">
                        <DownloadIcon className="tw-h-4 tw-w-4" aria-hidden="true" />
                        {$t('Download')}
                    </Button>
                    <Button as="a" href={config.reconfigureUrl} variant="outline-neutral">
                        {$t('Reconfigure')}
                    </Button>
                </div>
            </div>
        </div>
    );
}

/**
 * Budgets by Tag on screen: a version's total expenditure split by the items of one
 * codeset, each item expanding to departments and lines.
 */
export default function BudgetsByTagReport({ config }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        axios
            .get(config.dataUrl, { headers: { Accept: 'application/json' } })
            .then((response) => setData(response.data.data))
            .catch(() => setError($t('The report could not be loaded. Please try again.')));
    }, []);

    const notes = data
        ? [
              data.empty_message
                  ? null
                  : $t('Taggable items with no tagged lines show zeros. Click a tag to expand to department and line, or open its tag view.'),
              data.empty_message ? null : $t('Budget lines only; excludes workforce.'),
              data.actuals_note,
              data.figures_note,
          ].filter(Boolean)
        : [];

    return (
        <div className="tw-flex tw-flex-col tw-gap-y-4">
            <Header config={config} title={data?.title ?? $t('Budgets by Tag')} />

            {error && <Alert type="error">{error}</Alert>}

            {!data && !error && <LoadingSpinner />}

            {data && (
                <>
                    <p className="tw-m-0 tw-text-sm tw-text-charcoal-75">{data.basis}</p>
                    {data.empty_message ? (
                        <div className="tw-rounded-lg tw-border-2 tw-border-grey tw-p-6 tw-text-center tw-text-charcoal-75">{data.empty_message}</div>
                    ) : (
                        <BudgetsByTagTable data={data} />
                    )}
                    {notes.map((note) => (
                        <p key={note} className="tw-m-0 tw-text-xs tw-text-charcoal-75">
                            {note}
                        </p>
                    ))}
                </>
            )}
        </div>
    );
}

const rootEl = document.getElementById('BudgetsByTagReport');
if (rootEl) {
    ReactDOM.render(
        <App>
            <BudgetsByTagReport config={JSON.parse(rootEl.getAttribute('data-config') || '{}')} />
        </App>,
        rootEl,
    );
}
